/**
 * A walkthrough's context (spec 33 section 5.5): the handful of values its
 * script needs that differ in every visitor's tenant.
 *
 * A definition never names a customer, a location or a driver outright: a
 * regional fleet calls its customers something else, and the visitor may
 * have renamed or deleted anything. It writes tokens instead, `{customer}`,
 * `{pickup}`, `{po}`, `{at:+120}`, and the host fills them from the visitor's
 * own fleet (lib below + TourHost's resolvers) just before the step runs.
 *
 * Pure: no DOM, no network. The resolvers that ask the API live in
 * resolvers.js; this file only turns what they found into the strings typed.
 */

const TOKEN = /\{([a-zA-Z]+)(?::([+-]?\d+))?\}/g;

/** Two digits. */
const pad = n => String(n).padStart(2, '0');

/**
 * A datetime-local value `offsetMinutes` from `now`, rounded UP to the next
 * quarter hour, in the browser's own zone (which is the zone the stop editor
 * reads a datetime-local in). Rounded so the typed time looks like one a
 * dispatcher would type, never "14:07".
 */
export function localDateTime(offsetMinutes, now = new Date()) {
  const t = new Date(now.getTime() + offsetMinutes * 60000);
  const quarter = 15 * 60000;
  const rounded = new Date(Math.ceil(t.getTime() / quarter) * quarter);
  return (
    `${rounded.getFullYear()}-${pad(rounded.getMonth() + 1)}-${pad(rounded.getDate())}` +
    `T${pad(rounded.getHours())}:${pad(rounded.getMinutes())}`
  );
}

/**
 * The same moment as `localDateTime`, said the way a person reads a clock:
 * "2:15 PM today", "6:00 AM tomorrow". It is what a walkthrough's hint shows
 * beside a date-and-time box, because "2026-10-12T14:15" is the box's format,
 * not anybody's.
 */
export function friendlyTime(offsetMinutes, now = new Date()) {
  return friendlyOf(localDateTime(offsetMinutes, now), now);
}

/** A datetime-local value at a set time of day, `days` from today: (1, '08:00') is tomorrow at 8. */
export function localDateTimeOn(days, hhmm, now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${hhmm}`;
}

/** A datetime-local value said the way a person reads a clock ("8:00 AM tomorrow"). */
export function friendlyOf(local, now = new Date()) {
  const [date, hhmm] = String(local).split('T');
  const [h, m] = hhmm.split(':').map(Number);
  const clock = `${((h + 11) % 12) + 1}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`;
  const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  if (date === ymd(now)) return `${clock} today`;
  if (date === ymd(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1))) {
    return `${clock} tomorrow`;
  }
  return `${clock} on ${date}`;
}

/**
 * The PO the walkthrough types for the load it creates: recognisably the
 * walkthrough's (WT-), short, and different on every run so a second run in
 * the same tenant addresses its own row on the board, not the first one's.
 */
export function makePo(random = Math.random) {
  return `WT-${String(1000 + Math.floor(random() * 9000))}`;
}

// ── sample records the Your company walkthroughs add (spec 53 WP7) ──
/**
 * Customers the Add a customer walkthrough may add: made up, and named after
 * no place, because the card is filmed once and practised in every hub.
 * The first the tenant has not got is the one typed (a customer's name is
 * unique in a tenant, archived ones included), so a retry adds a new one.
 */
export const SAMPLE_CUSTOMERS = Object.freeze([
  'Bluebird Packaging',
  'Copperleaf Beverage',
  'Tall Pine Foods',
  'Northline Paper Supply',
  'Meadowlark Produce',
  'Silverline Plastics',
]);

/** The first of `samples` not in `taken` (names, any case), or null. */
export function firstUnused(samples, taken = []) {
  const have = new Set(taken.map(name => String(name || '').toLowerCase()));
  return samples.find(name => !have.has(name.toLowerCase())) || null;
}

/** The first sample customer name not in `taken` (names, any case), or null. */
export function newCustomerName(taken = []) {
  return firstUnused(SAMPLE_CUSTOMERS, taken);
}

const digits = (random, n) =>
  Array.from({ length: n }, () => String(Math.floor(random() * 10))).join('');

/** A unit number for a new truck: T and four digits, like the number on a door. */
export function makeUnit(random = Math.random) {
  return `T${1000 + Math.floor(random() * 9000)}`;
}

/** A plate: a digit, three letters, three digits. A sample, nobody's. */
export function makePlate(random = Math.random) {
  const letters = 'ABCDEFGHJKLMNPRSTUVWXYZ';
  const letter = () => letters[Math.floor(random() * letters.length)];
  return `${digits(random, 1)}${letter()}${letter()}${letter()}${digits(random, 3)}`;
}

// ISO 3779's check digit: each character's value, by position weight, mod 11.
const VIN_CHARS = 'ABCDEFGHJKLMNPRSTUVWXYZ0123456789';
const VIN_VALUE = {
  ...Object.fromEntries('ABCDEFGH'.split('').map((c, i) => [c, i + 1])),
  ...Object.fromEntries('JKLMN'.split('').map((c, i) => [c, i + 1])),
  P: 7,
  R: 9,
  ...Object.fromEntries('STUVWXYZ'.split('').map((c, i) => [c, i + 2])),
};
const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];

/** The check digit (position 9) a 17-character VIN should carry. */
export function vinCheckDigit(vin) {
  const sum = String(vin)
    .split('')
    .reduce((total, c, i) => total + (/\d/.test(c) ? Number(c) : VIN_VALUE[c]) * VIN_WEIGHTS[i], 0);
  const rest = sum % 11;
  return rest === 10 ? 'X' : String(rest);
}

/**
 * A sample VIN with a valid check digit (the form does not check one, but a
 * VIN that fails it looks wrong to anyone who knows). Position 10, the model
 * year, is left to chance like the rest: it is a sample.
 */
export function makeVin(random = Math.random) {
  const pick = () => VIN_CHARS[Math.floor(random() * VIN_CHARS.length)];
  const chars = Array.from({ length: 17 }, pick);
  chars[8] = '0';
  chars[8] = vinCheckDigit(chars.join(''));
  return chars.join('');
}

/** A colleague's address on example.com, which delivers to nobody, new on every run. */
export function makeInviteEmail(random = Math.random) {
  return `dispatch.${1000 + Math.floor(random() * 9000)}@example.com`;
}

/**
 * What to type into a search box to find `name`: its first word that is
 * long enough to be distinctive, the way a dispatcher types "Juniper" and not
 * "Juniper Ridge Produce Co." The picker then offers the full name. A whole
 * word, because the walkthrough now ASKS the visitor to type it ("Start
 * typing Cascadia"), and half a word reads as a typo.
 */
export function queryFor(name) {
  const words = String(name || '')
    .split(/\s+/)
    .map(w => w.replace(/[^\p{L}\p{N}'&-]/gu, ''))
    .filter(Boolean);
  const word = words.find(w => w.length >= 4) || words[0] || '';
  return word.slice(0, 16);
}

/**
 * Fill `{token}` and `{at:+minutes}` in a string, or in every string of an
 * object or array, from `context`. An unknown token is left as written, so a
 * missing value shows up as itself in a test rather than as "undefined".
 */
export function fill(value, context = {}, now = new Date()) {
  if (typeof value === 'string') {
    return value.replace(TOKEN, (whole, key, offset) => {
      if (key === 'at' && offset !== undefined) return localDateTime(Number(offset), now);
      if (key === 'time' && offset !== undefined) return friendlyTime(Number(offset), now);
      const found = context[key];
      return found === undefined || found === null ? whole : String(found);
    });
  }
  if (Array.isArray(value)) return value.map(v => fill(v, context, now));
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) out[k] = fill(v, context, now);
    return out;
  }
  return value;
}

/** Every token a definition (or any piece of one) uses, the clock ones aside. */
export function tokensIn(value, found = new Set()) {
  if (typeof value === 'string') {
    for (const match of value.matchAll(TOKEN)) {
      if (match[1] !== 'at' && match[1] !== 'time') found.add(match[1]);
    }
  } else if (Array.isArray(value)) {
    value.forEach(v => tokensIn(v, found));
  } else if (value && typeof value === 'object') {
    Object.values(value).forEach(v => tokensIn(v, found));
  }
  return found;
}

/** Read a dotted path ('data.id') out of a response body, or undefined. */
export function pick(body, path) {
  return String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((at, key) => (at === null || at === undefined ? undefined : at[key]), body);
}

// ── sample records for Add a location and Add a driver (spec 53) ────
/**
 * Places the Add a location walkthrough may add: named for what they are,
 * after no town or company, because the card is filmed once and practised
 * in every hub. The first the tenant has not got is the one typed, so a
 * retry adds a new one rather than a second of the same name.
 */
export const SAMPLE_LOCATIONS = Object.freeze([
  'Overflow Trailer Lot',
  'East Gate Yard',
  'Cross-Dock Annex',
  'North Parking Lot',
  'Back Dock Warehouse',
  'Second Drop Yard',
]);

/**
 * Drivers the Add a driver walkthrough may add: made-up people. A driver's
 * name is unique in a tenant, archived drivers included, so the first one
 * the tenant has not got is the one typed.
 */
export const SAMPLE_DRIVERS = Object.freeze([
  'Jordan Ellis',
  'Casey Morgan',
  'Riley Dawson',
  'Avery Lang',
  'Quinn Harper',
  'Morgan Tate',
]);

/** A phone number in the 555-01xx range, which is set aside for examples. */
export function makeDriverPhone(random = Math.random) {
  return `(555) 555-01${String(Math.floor(random() * 100)).padStart(2, '0')}`;
}

/**
 * The address a new location is given: a street address the map already
 * placed for one of this tenant's own locations (`locations`, GET
 * /locations), so it is a real one near the trainee's hub that the geocoder
 * finds. One whose first line starts with a house number if any has (an
 * exact match needs one; one hub's addresses are all road and kilometre),
 * the first by name, on one line, as a person types it. Null when none is
 * placed at all.
 */
export function addressNearby(locations = []) {
  const placed = locations
    .filter(l => l && l.latitude !== null && l.latitude !== undefined && l.address)
    .map(l => ({
      name: String(l.name || ''),
      lines: String(l.address)
        .split(/\r?\n/)
        .map(line => line.trim())
        .filter(Boolean),
    }))
    .filter(l => l.lines.length)
    .sort((a, b) => a.name.localeCompare(b.name));
  const numbered = placed.find(l => /^\d/.test(l.lines[0]));
  const pick = numbered || placed[0];
  return pick ? pick.lines.join(', ') : null;
}
